import { PHASES, TEMPLATES } from "./data";
import { reviewIssues } from "./planningDetails";

export const COMPASS_PROGRESS_SCHEMA_VERSION = 2;

export const EMPTY_PROGRESS = {
  schemaVersion: COMPASS_PROGRESS_SCHEMA_VERSION,
  productName: "",
  profileId: "dry-seasoning",
  startingPhase: 1,
  completedPhases: [],
  checklists: {},
  phaseRecords: {},
  readinessReconfirmationRequired: [],
};

const validPhaseNumbers = new Set(PHASES.map((phase) => phase.n));

function validPhaseList(value) {
  if (!Array.isArray(value)) return [];
  return [...new Set(value.filter((n) => Number.isInteger(n) && validPhaseNumbers.has(n)))].sort((a, b) => a - b);
}

export function normalizeCompassProgress(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return { ...EMPTY_PROGRESS };
  const completedPhases = validPhaseList(value.completedPhases);
  const legacy = value.schemaVersion !== COMPASS_PROGRESS_SCHEMA_VERSION;
  return {
    ...EMPTY_PROGRESS,
    ...value,
    schemaVersion: COMPASS_PROGRESS_SCHEMA_VERSION,
    startingPhase: validPhaseNumbers.has(value.startingPhase) ? value.startingPhase : 1,
    completedPhases,
    checklists: value.checklists && typeof value.checklists === "object" ? value.checklists : {},
    phaseRecords: value.phaseRecords && typeof value.phaseRecords === "object" ? value.phaseRecords : {},
    readinessReconfirmationRequired: validPhaseList(
      legacy ? completedPhases : value.readinessReconfirmationRequired
    ),
  };
}

export function hasMeaningfulDraft(value) {
  if (typeof value === "string") return value.trim().length > 0;
  if (typeof value === "number" || typeof value === "boolean") return true;
  if (Array.isArray(value)) return value.some(hasMeaningfulDraft);
  if (value && typeof value === "object") return Object.values(value).some(hasMeaningfulDraft);
  return false;
}

export function templateHasRequiredInformation(template, values) {
  if (!template || !values || typeof values !== "object" || Array.isArray(values)) return false;
  if (reviewIssues(values).length) return false;
  if (Array.isArray(template.sections)) {
    return template.sections.every((section) => {
      const fieldsReady = (section.fields || [])
        .filter((field) => field.required !== false)
        .every((field) => hasMeaningfulDraft(values[field.id]));
      if (!fieldsReady || !section.table) return fieldsReady;
      const rows = values[section.table.id];
      const requiredColumns = section.table.columns.filter((column) => column.required !== false);
      return Array.isArray(rows) && rows.some((row) =>
        row && requiredColumns.every((column) => hasMeaningfulDraft(row[column.id]))
      );
    });
  }
  return (template.fields || [])
    .filter((field) => typeof field === "string" || field.required !== false)
    .every((field) => hasMeaningfulDraft(values[typeof field === "string" ? field : field.id]));
}

export function validEvidence(record) {
  return Boolean(
    record?.evidenceDetails?.trim()
    && (record?.evidenceReviewer?.trim() || record?.noEvidenceReviewer)
  );
}

export function validQuestionRecord(record) {
  if (record?.noUnresolvedQuestions) return true;
  return Boolean(
    record?.questionsDetails?.trim()
    && record?.questionsOwner?.trim()
  );
}

export function phaseReadiness(progress, phase, drafts = {}) {
  const checked = new Set((progress.checklists?.[phase.n] || []).filter(Number.isInteger));
  const checklistReady = phase.checklist.every((_, index) => checked.has(index))
    && checked.size === phase.checklist.length;
  const templatesReady = phase.templates.every((id) =>
    templateHasRequiredInformation(TEMPLATES.find((template) => template.id === id), drafts[id])
  );
  const record = progress.phaseRecords?.[phase.n] || {};
  const reviewsReady = phase.templates.every((id) => !reviewIssues(drafts[id]).length);
  return {
    checklistReady,
    templatesReady,
    evidenceReady: validEvidence(record),
    questionsReady: validQuestionRecord(record),
    reviewsReady,
    ready: checklistReady && reviewsReady && (templatesReady || validEvidence(record)) && validQuestionRecord(record),
  };
}

export function effectiveCompleted(progress) {
  const reconfirm = new Set(progress.readinessReconfirmationRequired || []);
  return (progress.completedPhases || []).filter((n) => !reconfirm.has(n));
}

export function currentPhaseNumber(progress) {
  const done = new Set(effectiveCompleted(progress));
  return PHASES.find((phase) => !done.has(phase.n))?.n || PHASES[PHASES.length - 1].n;
}

export function completePhase(progress, phaseN) {
  if (phaseN !== currentPhaseNumber(progress)) return progress;
  return {
    ...progress,
    completedPhases: validPhaseList([...(progress.completedPhases || []), phaseN]),
    readinessReconfirmationRequired: (progress.readinessReconfirmationRequired || []).filter((n) => n !== phaseN),
  };
}

export function reopenPhase(progress, phaseN) {
  return {
    ...progress,
    completedPhases: (progress.completedPhases || []).filter((n) => n < phaseN),
    readinessReconfirmationRequired: (progress.readinessReconfirmationRequired || []).filter((n) => n < phaseN),
  };
}

// Any saved worksheet change invalidates affected completed stages, including
// changes made outside the roadmap. Keep all drafts, evidence and checked items.
export function reconfirmAfterWorksheetEdit(progress, templateId, before, after) {
  if (JSON.stringify(before || {}) === JSON.stringify(after || {})) return progress;
  const phase = PHASES.find((p) => p.templates.includes(templateId));
  const from = phase?.n || (templateId === "finished-product-spec" ? 2 : null);
  if (!from) return progress;
  const affected = (progress.completedPhases || []).filter((n) => n >= from);
  if (!affected.length) return progress;
  return {
    ...progress,
    readinessReconfirmationRequired: [...new Set([...(progress.readinessReconfirmationRequired || []), ...affected])].sort((a, b) => a - b),
  };
}